import { LuCircleAlert } from 'react-icons/lu'

import { Button } from '@/lib/core/components/Button'
import { FeatureModal } from '@/lib/core/components/Modal'

import { FeatureTypes } from '@/lib/utils/features'

import { TaskDefinition, TaskVehicleAvailabilityDefinition } from '../../types/task'
import styles from './AdditionalAssignVehicleModal.module.css'
import { computeActiveTask, computeConflictTasks } from '../AssignedVehiclePopout'
import { ConflictTaskSlider } from './ConflictTaskSlider'
import { AssignVehicleTaskDetails } from './AssignVehicleDetails'

interface AdditionalAssignVehicleModalProps {
  isOpen: boolean
  task: TaskDefinition
  selectedVehicle: TaskVehicleAvailabilityDefinition
  testId: string
  onClose: () => void
  onAssign: (vehicleId: number) => void
}

export const AdditionalAssignVehicleModal = ({
  isOpen,
  task,
  selectedVehicle,
  testId,
  onClose,
  onAssign,
}: AdditionalAssignVehicleModalProps) => {
  const conflictTasks = computeConflictTasks(selectedVehicle, task)
  const activeTask = computeActiveTask(selectedVehicle, task)
  const hasConflicts = conflictTasks && conflictTasks.length > 0

  if (!hasConflicts && !activeTask) {
    return null
  }

  if (hasConflicts) {
    return (
      <FeatureModal
        isActive={isOpen}
        feature={FeatureTypes.VEHICLE}
        title="Task scheduling conflicts"
        onClose={onClose}
        containerClassName={styles.assignedVehicleModal}
      >
        <div
          data-testid={`${testId}-assign-addition-task-modal-conflicts`}
          className={styles.assignedVehicleModalContentWrapper}
        >
          <div className={styles.conflictHeader}>
            <LuCircleAlert size={48} color="#EB5757" />
            <p className={styles.conflictDescription}>
              Looks like Vehicle <strong>{selectedVehicle.plateNumber}</strong> is already booked
              for <strong>{conflictTasks.length}</strong> other tasks during this time. Please
              choose a different vehicle or adjust the task timing.
            </p>
          </div>
          <ConflictTaskSlider tasks={conflictTasks} />
        </div>
        <div className={styles.assignedVehicleModalFooter}>
          <div className={styles.assignedVehicleModalFooterButtons}>
            <Button
              className={styles.assignedVehicleModalFooterButtonFull}
              variant="ghost"
              onClick={onClose}
              style={{ width: '100%' }}
            >
              Close
            </Button>
          </div>
        </div>
      </FeatureModal>
    )
  }

  return (
    <FeatureModal
      isActive={isOpen}
      feature={FeatureTypes.VEHICLE}
      title="Assign Vehicle to an additional task"
      onClose={onClose}
      containerClassName={styles.assignedVehicleModal}
    >
      <div
        data-testid={`${testId}-assign-addition-task-modal`}
        className={styles.assignedVehicleModalContentWrapper}
      >
        <div className={styles.assignedVehicleModalContent}>
          <div>
            <span>{selectedVehicle.plateNumber}</span> is currently allocated to Task ID:{' '}
            <span>{activeTask?.taskDefinition.reference}</span>.
          </div>
          <AssignVehicleTaskDetails displayTask={activeTask!} showStatus={true} />
        </div>
      </div>
      <div className={styles.assignedVehicleModalFooter}>
        <div className={styles.assignedVehicleModalFooterButtons}>
          <Button
            className={styles.assignedVehicleModalFooterButton}
            variant="ghost"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            className={styles.assignedVehicleModalFooterButton}
            variant="secondary"
            onClick={() => onAssign(selectedVehicle.id)}
          >
            Assign Vehicle
          </Button>
        </div>
      </div>
    </FeatureModal>
  )
}
